/**
 * Minimal data-fetching hooks. No TanStack Query yet — the console's needs are
 * a handful of lists and forms, and keeping the dependency surface small matters
 * for a project that must stay buildable on modest infra (ADR-0009 ethos).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface QueryState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  /** Re-run the fetcher, keeping the current data visible while it runs. */
  refetch: () => void;
}

/**
 * Runs `fetcher` on mount and whenever a value in `deps` changes. Optionally
 * re-runs every `pollMs` milliseconds (used by the Display view).
 */
export function useQuery<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
  options: { pollMs?: number } = {},
): QueryState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [...deps, tick]);

  useEffect(() => {
    if (!options.pollMs) return;
    const id = setInterval(refetch, options.pollMs);
    return () => clearInterval(id);
  }, [options.pollMs, refetch]);

  return { data, error, loading, refetch };
}

export interface MutationState<A extends unknown[], R> {
  run: (...args: A) => Promise<R>;
  pending: boolean;
  error: Error | undefined;
  reset: () => void;
}

/** Wraps an async action with pending/error tracking for form submits. */
export function useMutation<A extends unknown[], R>(
  action: (...args: A) => Promise<R>,
): MutationState<A, R> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error>();
  const actionRef = useRef(action);
  actionRef.current = action;

  const run = useCallback(async (...args: A): Promise<R> => {
    setPending(true);
    setError(undefined);
    try {
      return await actionRef.current(...args);
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      throw wrapped;
    } finally {
      setPending(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(undefined);
  }, []);

  return { run, pending, error, reset };
}
